import { createNewsAdapter, NEWS_TIME_LIST_CONFIG } from './newsAdapter';
import type { NewsTimeList } from './newsAdapter';
import { effect } from '@preact/signals-core';
import type { ReadonlySignal } from '@preact/signals-core';
import type { QueryClient } from '@tanstack/query-core';
import { SymbolTrackedTimeList } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { TimeListScheduler } from '@thinkalpha/ui-symbol-tracked-time-list';
import { createTimeListTransport, getNewsPageQuery } from 'src/queries/timeList';

const scheduler: TimeListScheduler = {
    now: () => Date.now(),
    schedule(delay, callback) {
        const timer = setTimeout(callback, delay);
        return () => clearTimeout(timer);
    },
};

export function createNewsList(client: QueryClient): NewsTimeList {
    return new SymbolTrackedTimeList({
        source: {
            sourceId: 'news',
            selectionKind: 'multi-symbol',
            mode: 'live',
            query: { anchor: null },
            retention: 0,
        },
        adapter: createNewsAdapter(),
        config: NEWS_TIME_LIST_CONFIG,
        scheduler,
        transport: createTimeListTransport(client, getNewsPageQuery),
    });
}

/** Embedded views use the same entity; changing the selected symbol starts a new source. */
export class SingleSymbolNewsModel implements Disposable {
    readonly list: NewsTimeList;
    readonly #disposables = new DisposableStack();
    constructor(client: QueryClient, symbol: ReadonlySignal<string>) {
        this.list = this.#disposables.use(createNewsList(client));
        this.#disposables.defer(
            effect(() => {
                const value = symbol.value;
                this.list.activateSource(
                    {
                        sourceId: value,
                        selectionKind: 'single-symbol',
                        mode: 'live',
                        query: { anchor: null },
                        retention: 0,
                    },
                    value ? [value] : [],
                );
            }),
        );
    }
    [Symbol.dispose](): void {
        this.#disposables.dispose();
    }
}
